import { logger } from '../logging/logger.js';
import { MemberContactDetails, MemberDetailEntity, MemberProfile, MemberSearchEntity, parseMemberUserId } from '../models/member.js';
import type { MemberPayment, MemberPaymentsRequest } from '../models/payment.js';
import type { UserAvatarResponse } from '../models/user.js';
import { mapResponseToActivity, type DonationEvent, type MemberActivityResponse } from '../models/donation-event.js';
import { apiClient, parseOptionalIntHeader } from './client.js';
import { DonateSearchOptions } from './donate-admin.js';

export interface MemberPaymentsResponse {
  data: MemberPayment[]
  total: number
}

export async function getMemberDetail(
  memberId: number,
  options: DonateSearchOptions = {},
): Promise<MemberDetailEntity> {
  return await apiClient.get<MemberDetailEntity>(`/donate/v1/members/${encodeURIComponent(memberId)}`, {
    withAuth: true,
    signal: options.signal,
  });
}

export async function getMyMemberProfile(
  options: DonateSearchOptions = {},
): Promise<MemberProfile> {
  return await apiClient.get<MemberProfile>('/donate/v1/members/me', {
    withAuth: true,
    signal: options.signal,
  });
}

export async function getMyMemberPayments(
  params: MemberPaymentsRequest = {},
  options: DonateSearchOptions = {},
): Promise<MemberPaymentsResponse> {
  const body: MemberPaymentsRequest = {};
  if (typeof params.page === 'number') body.page = params.page;
  if (typeof params.per_page === 'number') body.per_page = params.per_page;
  if (params.sort_order) body.sort_order = params.sort_order;
  const { data, response } = await apiClient.postWithResponse<MemberPayment[]>(
    '/donate/v1/members/me/payments',
    body,
    { withAuth: true, signal: options.signal },
  );
  const total = parseOptionalIntHeader(response.headers, 'x-total') ?? data.length;
  return { data, total };
}

export interface ShippingAddressPayload {
  country: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
}

export async function updateMyShippingAddress(
  memberId: number,
  shippingAddress: ShippingAddressPayload,
  options: DonateSearchOptions = {},
): Promise<MemberProfile> {
  return await apiClient.put<MemberProfile>(
    `/donate/v1/members/${encodeURIComponent(memberId)}`,
    { shipping_address: shippingAddress },
    { withAuth: true, signal: options.signal },
  );
}

export interface SearchMembersRequest {
  q: object;
  page?: number;
  per_page?: number;
}

export async function searchMembers(
  request: SearchMembersRequest,
  options: DonateSearchOptions = {},
): Promise<MemberSearchEntity[]> {
  return await apiClient.post<MemberSearchEntity[]>(
    '/donate/v1/members/search',
    request,
    { withAuth: true, signal: options.signal },
  );
}

export interface UpdateMemberDetailPayload {
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  login_phone_number?: string;
  partner_first_name?: string;
  partner_last_name?: string;
  newsletter?: boolean;
  shipping_address?: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  create_user_account?: boolean;
}

export async function updateMemberDetail(
  memberId: number,
  payload: UpdateMemberDetailPayload,
  options: DonateSearchOptions = {},
): Promise<MemberDetailEntity> {
  return await apiClient.put<MemberDetailEntity>(
    `/donate/v1/members/${encodeURIComponent(memberId)}`,
    payload,
    { withAuth: true, signal: options.signal },
  );
}

const buildAddressLines = (member: MemberDetailEntity): string[] | null => {
  const address = member.shipping_address ?? member.mailing_address

  const line1 = address?.street?.trim() ?? ''
  const city = address?.city?.trim() ?? ''
  const state = address?.state?.trim() ?? ''
  const zip = address?.postal_code?.trim() ?? ''

  if (!line1 && !city && !state && !zip) return null

  const stateZip = [state, zip].filter(Boolean).join(' ')
  const line2 = city && stateZip ? `${city}, ${stateZip}` : city || stateZip

  const lines: string[] = []
  if (line1) lines.push(line1)
  if (line2) lines.push(line2)
  return lines.length > 0 ? lines : null
}

export async function fetchMemberContactDetail(memberId: number, options?: { signal?: AbortSignal }): Promise<MemberContactDetails | null> {
  try {
    const data = await getMemberDetail(memberId, { signal: options?.signal })

    const userIdNum = parseMemberUserId(data.user_id)

    const userId = typeof userIdNum === 'number' ? String(userIdNum) : null
    const phoneCandidate = (data.phone_number ?? data.login_phone_number ?? '').trim()
    const phone = phoneCandidate.length > 0 ? phoneCandidate : null
    const addressLines = buildAddressLines(data)

    let avatarUrl: string | null = null
    if (typeof userIdNum === 'number') {
      try {
        const user = await apiClient.get<UserAvatarResponse>(`/v1/users/${encodeURIComponent(String(userIdNum))}`, {
          withAuth: true,
          signal: options?.signal,
        })
        avatarUrl = typeof user.cdn_url === 'string' && user.cdn_url.trim().length > 0 ? user.cdn_url.trim() : null
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') throw error
        logger.error('Failed to load user avatar url for member contact detail', { memberId, userId: userIdNum, error })
        avatarUrl = null
      }
    }

    return { addressLines, phone, userId, avatarUrl }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error
    }
    logger.error('Failed to load member contact detail', { memberId, error })
    return null
  }
}

export async function getMyActivity(
  params: { page?: number; per_page?: number; event_types?: string[] } = {},
  options: DonateSearchOptions = {},
): Promise<MemberActivityResponse> {
  const toPositiveInt = (value: number | undefined, fallback: number): number =>
    typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : fallback;
  const page = toPositiveInt(params.page, 1);
  const perPage = toPositiveInt(params.per_page, 25);
  const search = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  for (const type of params.event_types ?? []) search.append('event_types[]', type);

  const { data, response } = await apiClient.getWithResponse<unknown[]>(
    `/donate/v1/members/me/activity?${search.toString()}`,
    { withAuth: true, signal: options.signal },
  );
  const events = (data ?? []).map(mapResponseToActivity);
  const total = parseOptionalIntHeader(response.headers, 'x-total') ?? events.length;
  return { data: events, total };
}

export async function getMyActivityEvent(
  id: number | string,
  options: DonateSearchOptions = {},
): Promise<DonationEvent> {
  const raw = await apiClient.get<unknown>(
    `/donate/v1/members/me/activity/${encodeURIComponent(id)}`,
    { withAuth: true, signal: options.signal },
  );
  return mapResponseToActivity(raw);
}